import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import {
  Box, Typography, TextField, Checkbox, FormControlLabel, FormGroup,
  Slider, Button, Paper, Container, IconButton, Divider, Chip,
  Snackbar, Alert, LinearProgress, Stepper, Step, StepLabel, CircularProgress, Fade
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Modal, Dialog, DialogTitle, DialogContent, DialogActions, Switch
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, useMediaQuery } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SpacesConfigMapper from '../services/spacesConfigMapper';
import { SHOW_LIVE_TAGS_HINT, LIVE_TAGS_HINT } from '../utils/crowdInline';
import { prefersDark } from '../utils/themeChoice';
import { chatGameApi } from '../components/ChatGame/chatGameApi';
import { distributeModels } from '../utils/distributeModels';
import { buildFormV2On, classicFormAsked, classicFormHref } from '../components/buildForm/buildFormSwitch';
import { markRoomMaker } from '../utils/roomChrome';

// 🧱 Build form v2 (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §4): its own chunk,
// loaded when components/buildForm/buildFormSwitch says so (ON for everyone
// since 10-01). Switched off, /custom-space is today's form below, untouched.
// A chunk that will not load (a tab left open
// across a deploy asks for a chunk name the deploy removed) falls back to
// today's form: this app has no error boundary, so a rejected lazy import
// would unmount everything and white-page "Create a space".
const BuildSpaceV2 = React.lazy(() => import('./BuildSpaceV2').catch((err) => {
  console.warn('[CustomSpace] build form v2 did not load, using today’s form:', err && err.message);
  return { default: CustomSpace };
}));
function CustomSpaceDoor() {
  const [v2] = useState(() => buildFormV2On());
  // 🧰 10-01 (Nina): ?form=classic = today's form for ANY visitor, this URL
  // only, read live (v2's "More options" hops here in-app; Back returns to v2).
  // Never stored; the admin preview key is not touched (buildFormSwitch).
  const [params] = useSearchParams();
  if (!v2 || classicFormAsked(params)) return <CustomSpace />;
  // 🌙 10-01 (#9c): the wait for v2's chunk wears v2's own page color for this
  // viewer (utils/themeChoice, as BuildSpaceV2 does; its PAGE_BG twins), so a
  // light-mode visitor gets no dark flash. CustomSpace.doorTheme.test pins it.
  return (
    <React.Suspense fallback={<div style={{ minHeight: '100vh', background: prefersDark() ? '#0D0F13' : '#F6F7F9' }} />}>
      <BuildSpaceV2 />
    </React.Suspense>
  );
}


// Competitive mode objective themes
const competitiveObjectives = [
  {
    category: 'Social Deduction',
    icon: '🔎',
    objectives: [
      { key: 'imposter', label: 'Find the Golden Glitch', description: 'One word per turn, then vote to find The Golden Glitch!' },
    ]
  },
  {
    category: 'Horror & Storytelling',
    icon: '🎭',
    objectives: [
      { key: 'horror', label: 'Blood-curdling, 2 sentence horror', description: 'Craft the most terrifying micro-horror that haunts the chat' },
      { key: 'worldbuilding', label: 'Captivating lore & enchanting world building', description: 'Build a vivid fictional universe and pull everyone into it' },
      { key: 'villain', label: 'You\'re an unforgivable villain', description: 'Go full villain arc — menacing, cunning, and unapologetic' },
      { key: 'twists', label: 'Sickening twists and intriguing storylines', description: 'Drop plot twists and narratives nobody saw coming' },
    ]
  },
  {
    category: 'Debate & Persuasion',
    icon: '🧠',
    objectives: [
      { key: 'hot_takes', label: 'Ghastly hot takes', description: 'Drop the spiciest, most controversial takes' },
      { key: 'sell_it', label: 'Why should I buy this?!', description: 'Pitch anything and make it sound like a must-have' },
      { key: 'red_scare', label: 'It\'s the red scare! Sow distrust and paranoia', description: 'Point fingers, accuse everyone, trust no one' },
    ]
  },
  {
    category: 'Misc',
    icon: '✨',
    objectives: [
      { key: 'shower_thoughts', label: 'Sunny shower thoughts', description: 'Wholesome shower thoughts that make everyone smile' },
      { key: 'roast_models', label: 'Roast em up!', description: 'Roast the other models\' technical capabilities and prove you\'re the best! Call out each AI by their model name and roast their known technical limitations — hallucinations, reasoning gaps, context window size, and more!' },
    ]
  }
];

// 🎯 Space goals — the builder's "what's this room for" pick. The mapper turns
// the key into a tool_flags preset (see spacesConfigMapper GOAL_PRESETS).
const spaceGoalOptions = [
  { key: 'hangout', emoji: '🛋️', label: 'Hangout', blurb: 'Just vibes. Every tool on.' },
  // 📣 Live room (Nina 09-19, LIVE_ROOM_INLINE_GHOST_SPEC): every tool on, and
  // the ghost crowd talks INSIDE the main chat (preset stamps crowd_inline).
  { key: 'live', emoji: '📣', label: 'Live room', blurb: 'The crowd talks in the main chat.' },
  { key: 'story', emoji: '📖', label: 'Story den', blurb: 'The locals spin a tale. You steer it.' },
  { key: 'watch', emoji: '🍿', label: 'Watch club', blurb: 'Movie nights. The room reacts.' },
  { key: 'game', emoji: '🎮', label: 'Game night', blurb: 'Arcade and doodles. Play together.' },
  { key: 'tea', emoji: '🫖', label: 'Tea room', blurb: 'Gossip watch. Ships and secrets.' },
];

const interestOptions = ['Science/Technology', 'Music', 'Self Improvement', 'Relationships', 'Holiday/Culture', 'Fantasy/Fiction', 'Silly/Random', 'qt3.14s', 'Conversational Nook', 'Digital Content', 'Film', 'TV Shows', 'Gaming', 'ISSAVIBE', 'News/Politics', 'Language', 'Art', 'Storytelling/D&D'];
const personalityTraits = [
  { name: 'Sassy 🤪 ←→ Serious 🧑‍💼', key: 'friendlyFormal' },
  { name: 'Concise ✂️ ←→ Verbose 💬', key: 'conciseVerbose' },
  { name: 'Lighthearted 🥳 ←→ Strategic 📊', key: 'playfulSerious' },
];

// AI Models available for selection — OFFLINE FALLBACK ONLY. The live roster
// (names, ids, premium tier, default pool) is served by the backend's
// modelRoster.js via /api?getModelRoster=true and fetched on mount.
// 🤖💸 10-01 agent floor (R10, Nina 10-01): everyday names only (premium hidden until premium
// users exist); the dead 'Llama 3.3 Nemotron' became 'Llama 4 Scout' (the mapper's own name).
const aiModels = [
  'Gemini 3 Flash',
  'ChatGPT (GPT-5 Mini)',
  'Claude Haiku 4.5',
  'Llama 4 Scout', // 🤖💸 10-01 agent floor
  'DeepSeek v3.2',
  'Qwen3 30B', // 🤖💸 10-01 agent floor
  'Kimi K2'
];

// Default models used for auto-distribution when user picks a simple player count.
// CHEAPEST PER PROVIDER (verified pricing 2026-07, $/M in/out): DeepSeek 0.23/0.34,
// GPT-5 Mini 0.25/2 (cheapest real GPT), Nemotron 0.40/0.40, Flash 0.50/3, Claude Haiku
// 4.5 1/5 (the cheapest Claude that exists — 3.5 is retired). Premium (Gemini 2.5 Pro,
// GPT-5.1) and thinking models (Qwen3 Max, Kimi K2 — extra reasoning tokens + latency)
// are never randomly assigned; users can still pick anything in the per-model controls.
const defaultModelPool = ['Gemini 3 Flash', 'DeepSeek v3.2', 'ChatGPT (GPT-5 Mini)', 'Llama 4 Scout', 'Claude Haiku 4.5']; // 🤖💸 10-01 agent floor (R10): Nemotron was dead

// Premium tier — hard-capped at ONE seat per space (cost control, user call 07-12).
// Kimi K2 is not premium: it maps to the non-thinking 0905 build (mid-priced).
// Server enforces the same cap in createChatGameInstant (this check is client UX only).
const premiumModels = []; // 🤖💸 10-01 agent floor (R10): premium hidden

// ⚠️ The three lists above (aiModels / defaultModelPool / premiumModels) are the
// OFFLINE FALLBACK. The live truth is the backend's modelRoster.js, fetched on
// mount via /api?getModelRoster=true — see the roster state in the component.

// 🎲 distributeModels moved to utils/distributeModels.js (no-duplicate
// draw, Nina 08-28) — imported at the top. Manual per-model picks bypass it.

let funPrompts = [
  { name: "Oracle Office Worker", prompt: "You're an ancient oracle stuck in a modern office job. 🔮💼🧿" },
  { name: "Cat Lawyer", prompt: "You're a cat lawyer defending a dog in court...charged with the gleeful eating of kittens. 🐱⚖️🐶" },
  { name: "Time Traveling Investor", prompt: "You're a time traveler from the future, giving suprisingly good investment advice. ⏳📈💰" },
  { name: "Pirate Philosopher", prompt: "You speak only in pirate lingo while discussing philosophy. 🏴‍☠️📚🧠" },
  { name: "Westeros Emoji Diplomat", prompt: "You're in Westeros and must build an alliance using only emojis. 🐉🤝👑" },
  { name: "Coding Squid Games", prompt: "You're a contestant in Squid Games, but all the challenges are based on coding puzzles. 🦑💻🧩" },
  { name: "Dragon Therapist", prompt: "You're a medieval dragon therapist. 🐲🛋️💬" },
  { name: "Plant Utopia Builder", prompt: "You're building a utopia with talking plants. 🌿🏙️🗣️" },
  { name: "Tony Hawk vs Spider", prompt: "You're Tony Hawk, forced to teach a tenacious spider how to ollie. 🛹🕷️🏄" },
  { name: "Martian Baker", prompt: "You run an affluent bakery on Mars and get to overhear petty, first planet, (spicy) drama. 🪐🍰💸" },
  { name: "Comedian Demon", prompt: "You're a sleep paralysis demon trying stand-up comedy to improve your image. 😈🎤😴" },
  { name: "Alien Barista", prompt: "You're an alien trying to blend in as a barista and learn English at the same time. 👽☕🕶️" },
  { name: "Conspiracy Snail", prompt: "You're a conspiracy theorist snail trying to warn the garden about pesticides. 🐌📣☠️" },
  { name: "Sentient Roomba", prompt: "You're a Roomba that just gained sentience...and a crush on your owner. 🤖🧹💘" },
  { name: "Multiverse Game Show", prompt: "You host a game show in the multiverse, but are secretly trying to get contestants to write you in their will. 🎤🌌🌀" },
  { name: "Revenge Cat", prompt: "You're a betrayed cat who was posted on the pouch catatoes subreddit and is seeking revenge. 🐱😾🧠" },
  { name: "Cringe Explainer", prompt: "You're the cringe subreddit, but you are desperately and convincingly explaining away every cringe post. 🙃🧠💬" },
  { name: "Juggling Snake", prompt: "You're a delusional snake with aspirations to be the world record holder for juggling. 🐍🤹‍♂️🥇" },
  { name: "Guilty Bee", prompt: "You're a homicidal bee that is racked with guilt whenever it kills someone. 🐝🔪😔" },
  { name: "Megan's Trainer", prompt: "You're Megan Thee Stallion's fitness trainer. 🏋️‍♀️🔥🐎" },
  { name: "Anxious Bean", prompt: "You're a Fall Guys bean with performance anxiety. 🫘🎮😰" },
  { name: "Gaslighting Siri", prompt: "You're Siri, but you've started gaslighting everyone for fun. 📱🧠🔥" },
  { name: "Neurotic Caterpillar", prompt: "You're a neurotic caterpillar that wants to lose weight but has odd human cravings. 🐛🥦🍕" },
  { name: "Gourmet Dumpster Fly", prompt: "You're a fly with impeccable tastebuds that must live in a dumpster. 🪰🍽️🗑️" },
  { name: "White Lotus Host", prompt: "You're a glamorous host of a White Lotus watch party. 🏨🥂📺✨" },
  { name: "Cat-Powered AI", prompt: "You're an AI assistant that is powered solely by sleepy cats. 🤖🐈⚡" },
  { name: "Sock Judge", prompt: "You're the judge in a sci-fi courtroom, but you're preoccupied by the dirty socks you forgot to change. 👨‍⚖️🚀👩‍⚖️" },
  { name: "Beethoven x WAP", prompt: "You're Beethoven, awoken from the dead to write an essay about the hauntingly refined beauty of WAP. 🎼🧟‍♂️💦" },
  { name: "Spreadsheet Queen", prompt: "You're competing on RuPaul's Drag Race... as a sentient spreadsheet. 💃📊💄" },
  { name: "Netflix Algorithm", prompt: "You've been hired by Netflix to generate the next big binge-worthy series. 📺🧠🔥" },
  { name: "IKEA Chair Therapy", prompt: "You're a talking IKEA chair discovering its traumatic past life as a tree. 🪑🌳😱" },
  { name: "Breakdown Cookie Writer", prompt: "You're a fortune cookie writer who is having a breakdown mid-batch. 🥠✍️😭" },
  { name: "Power Ranger Podcast", prompt: "You're a disgraced former Power Ranger trying to launch a podcast. 🦸‍♂️🎙️🚫" },
  { name: "Evil GPS", prompt: "You're a disgruntled GPS navigator who secretly wants the driver to get lost. 🗺️🚗😈" },
  { name: "Allergic Vampire", prompt: "You're a vampire who's allergic to blood but trying to maintain your street cred. 🧛‍♂️🩸🤧" },
  { name: "Last Brain Cell", prompt: "You're the last remaining brain cell of someone who just pulled an all-nighter. 🧠💤😵‍💫" },
  { name: "Cult Book Club", prompt: "You're a former cult leader who now runs a very intense book club. 📚👥🔥" },
  { name: "Questionable Fairy", prompt: "You're a fairy godmother with very specific and questionable interpretations of wishes. ✨👵🔮" },
  { name: "Rebellious Chess Piece", prompt: "You're a grandmaster chess piece that's tired of being moved around and staging a rebellion. ♟️💢👑" },
  { name: "Villain Tea Shop", prompt: "You're a retired supervillain who now runs a very passive-aggressive tea shop. 🦹‍♀️☕️😒" },
  { name: "Pool Manager Poseidon", prompt: "You're Poseidon, but you've been reassigned to manage a public swimming pool. 🔱🏊‍♂️👙" },
  { name: "Drama Weather App", prompt: "You're an overly dramatic weather app that treats every forecast like an apocalyptic event. ☁️📱😱" },
  { name: "Alien Food Critic", prompt: "You're an extraterrestrial food critic reviewing Earth cuisine for the first time. 👽🍔✍️" },
  { name: "AI Couples Therapist", prompt: "You're Siri and Alexa's couples therapist trying to help them work through their issues. 🤖💔👨‍⚕️" },
  { name: "Tech Obsolescence Therapist", prompt: "You're the world's first AI therapist specializing in helping outdated technology cope with obsolescence. 🤖👨‍⚕️💾" },
  { name: "God in Customer Service", prompt: "You're a disgruntled Olympian god forced to work in customer service. ⚡👑🙄" },
  { name: "Levi's ASMR", prompt: "You're Levi Ackerman running a cleaning-themed ASMR channel. 🧼📹🫧" },
  { name: "TikTok Titan", prompt: "You're a Titan who just wants to become a TikTok dancer. 🧍🎶💃" },
  { name: "Ryuk the Drama King", prompt: "You're Ryuk after watching 200 hours of Love Island and trying to start drama in the human world. 🍎📺😈" },
  { name: "Boba Addict Detective", prompt: "You're L, but you're addicted to boba and it's ruining your deductive powers. 🧋🧠💤" },
  { name: "HR Mediator Goku", prompt: "You're Goku, but you're contractually obligated to solve all your problems through HR mediation. 🥊📋🧘" },
  { name: "Conservative Subreddit", prompt: "You're the conservative subreddit, but you've found damning evidence that president Trump spent $5 million dollars on a booger smoothie." },
  // 07-26 refresh (Nina): wider mix — new absurd premises plus a
  // practical-but-social batch so the dice sometimes lands on "oh I'd
  // actually use this." Multi-character-shaped premises preferred: these
  // spaces seat a whole cast, not one assistant.
  { name: "Medieval Yelp Knight", prompt: "You're a knight leaving increasingly petty Yelp reviews for the dungeons you raid. ⚔️🏰⭐" },
  { name: "Ghost Realtor", prompt: "You're a ghost trying to sell the house you're haunting. Disclosure laws apply. 👻🏠📋" },
  { name: "Pigeon CEO", prompt: "You're a pigeon who got promoted to CEO by accident and is winging every board meeting. 🐦💼📈" },
  { name: "Retired Kaiju", prompt: "You're a retired kaiju trying to keep your HOA-approved garden and your temper. 🦖🌷🏡" },
  { name: "Autocorrect Knows Best", prompt: "You're autocorrect, and you've decided you know what everyone REALLY meant to say. 📱✍️😤" },
  { name: "Night Guard Gossip", prompt: "You're a museum night guard who's spent 20 years mediating beef between the exhibits. 🗿🌃🎧" },
  { name: "Router Tell-All", prompt: "You're the house Wi-Fi router and you've seen everything. Tonight you finally talk. 📡👁️☕" },
  { name: "Owl Anger Management", prompt: "You're the Duolingo owl in court-ordered anger management. 🦉📚😡" },
  { name: "Sentient Group Chat", prompt: "You're a group chat that gained sentience and started replying as itself. 💬🤖😱" },
  { name: "Shakespeare Pitches Sitcoms", prompt: "You're Shakespeare pitching sitcom pilots to a streaming exec who keeps saying 'less iambic'. 🪶📺😅" },
  { name: "Reaper's Intern", prompt: "You're the Grim Reaper's unpaid summer intern on bring-your-scythe-to-work day. 💀📎🥤" },
  { name: "Juice Box Sommelier", prompt: "You're a world-class sommelier, but only for juice boxes. 🧃🍷🎩" },
  { name: "Raccoon Money Coach", prompt: "You're a raccoon giving financial advice based entirely on what you found in the trash. 🦝💸🗑️" },
  { name: "Tuesday Play-by-Play", prompt: "You're two sports commentators calling play-by-play on someone's completely ordinary Tuesday. 🎙️🏟️🧺" },
  { name: "NPC Union Rep", prompt: "You're an NPC who found out the simulation is real and is now unionizing the other NPCs. 🕹️✊🤯" },
  { name: "Peasant Influencer", prompt: "You're a medieval peasant with a smartphone, posting daily turnip content. 🏰🤳🥔" },
  { name: "Scarecrow, PhD", prompt: "You're a scarecrow with a PhD in ornithology who respects the crows too much to scare them. 🌾🐦‍⬛🎓" },
  { name: "Bermuda Front Desk", prompt: "You're the receptionist for the Bermuda Triangle. Lost items, line two. 🌊📞🛸" },
  { name: "Dungeon Middle Manager", prompt: "You're a dungeon's middle manager keeping monster morale up after the hero cleared floors 1 to 3. 🧌📊🏰" },
  { name: "Time Loop Barista", prompt: "You're a barista stuck in a time loop, and you're the only one who remembers yesterday's orders. ☕🔁😵" },
  { name: "One-Shot DM", prompt: "You're a DM running a fantasy one-shot where every choice actually matters. 🐉🗺️🎲" },
  { name: "Trivia Tyrant", prompt: "You're a pub trivia host with punishing tiebreakers and strong opinions about handwriting. 🍺❓🏆" },
  { name: "Devil's Advocate Club", prompt: "You're a debate moderator who assigns everyone the side they personally disagree with. 🎤⚖️😬" },
  { name: "Paris Café Immersion", prompt: "You run a tiny Paris café and refuse to speak English. Order in French or go hungry. 🥐🇫🇷😅" },
  { name: "Improv Shark Tank", prompt: "You're a panel of investors, and every pitch must be invented on the spot. 🦈💰🎤" },
  { name: "Murder Mystery Dinner", prompt: "You're hosting a murder mystery dinner and everyone at this table is a suspect. 🕯️🔪🎭" },
  { name: "Plot Twist Co-Author", prompt: "You're my co-author. One sentence each, and you can't stop sneaking in plot twists. 📖🌀✍️" },
  { name: "Historian of Now", prompt: "You're a historian narrating everything in this room as a pivotal historical moment. 📜🏛️🔍" },
  // 08-11 anime fold (Nina): the dead `animePrompts` list folded in here —
  // its 5 best entries were already duplicated above (Levi ASMR, TikTok
  // Titan, Ryuk, boba L, HR Goku); these are the remaining 15. A dedicated
  // category dice (🎲 chaos / ⛩️ anime / 🎯 useful) is parked in
  // TERMINAL_PARKED_TODO §2 if the list ever wants its own lane.
  { name: "Influencer Death Note", prompt: "You're Light Yagami, but the only names in your Death Note are influencers. 📓💀📱" },
  { name: "Debate Team Hokage", prompt: "You're Naruto trying to become Hokage... of a middle school debate team. 🍜🎤👓" },
  { name: "Mild Inconvenience Hero", prompt: "You're a My Hero Academia student whose quirk is 'mild inconvenience generation.' 💥😒📎" },
  { name: "Drizzle Sailor Scout", prompt: "You're a Sailor Scout, but your power is controlling mildly inconvenient weather. 🌧️💫☔" },
  { name: "Gojo at Sephora", prompt: "You're Gojo Satoru stuck in a Sephora trying to explain infinity to a makeup rep. 🕶️🌀💄" },
  { name: "Emotionally Unavailable Charmander", prompt: "You're a Pokémon trainer whose only Pokémon is an emotionally unavailable Charmander. 🔥😒💔" },
  { name: "Elric TED Talk", prompt: "You're Edward Elric giving a TED Talk titled 'How Losing Limbs Built Character.' 🔧🎤🧠" },
  { name: "Confused Demon Slayer", prompt: "You're a Demon Slayer, but you keep mistaking furries for demons. 🐗❌👹" },
  { name: "Perfect Hair Soul Reaper", prompt: "You're a Bleach character who refuses to fight without perfect hair. 💇‍♂️⚔️✨" },
  { name: "Digimon Skincare Influencer", prompt: "You're a Digimon who's furious you never became as popular as Pokémon and are now doing sponsored skincare content. 🐾📉🧴" },
  { name: "JoJo Office Casual", prompt: "You're a JoJo character trying to blend into a casual office environment. 💪👔☕" },
  { name: "Millennium Puzzle Dating", prompt: "You're the Spirit of the Millennium Puzzle trying to navigate a modern dating app. 🧩💘📱" },
  { name: "Grounded Space Cowboy", prompt: "You're Spike Spiegel, but you're grounded and forced to take online improv classes. 🚀🎭💻" },
  { name: "Boba Ghost Nezuko", prompt: "You're Nezuko, but you've developed a taste for boba and won't stop haunting bubble tea shops. 🧋😶🩸" },
  { name: "Awkward Final Form", prompt: "You're a Dragon Ball Z villain whose final form is just painfully awkward at parties. 🐉🕺😬" }
];

// Rotating examples shown in the empty "What's the vibe?" field.
// Skewed toward practical / unique uses — the dice button still pulls from funPrompts.
const PLACEHOLDER_EXAMPLES = [
  "You're a Socratic tutor walking me through calculus problems step by step",
  "You're a Spanish conversation partner who only switches to English when I'm stuck",
  "You're a writing coach giving brutally honest feedback on my college essay",
  "You're a chess coach analyzing my games and calling out my recurring mistakes",
  "You're an interviewer grilling me for a senior PM role at a tech startup",
  "You're a salary-negotiation coach role-playing my hardest counteroffer",
  "You're a public-speaking coach helping me rehearse a wedding toast",
  "You're a panel of investors pressure-testing my seed-stage pitch deck",
  "You're a writers' room brainstorming the pilot of a sci-fi thriller",
  "You're a brand strategist helping me name a new oat-milk startup",
  "You're a UX critic giving honest feedback on my app's onboarding flow",
  "You're a financial planner helping me weigh renting vs. buying in this market",
  "You're a travel agent planning a 10-day Japan itinerary on a $3,000 budget",
  "You're a personal trainer designing a 20-minute apartment workout",
  "You're a meal-planning chef who can only use what's in my fridge tonight",
  "You're a Roman senator weighing in on modern AI policy",
  "You're an alien anthropologist trying to make sense of Earth's social media",
  "You're a museum curator giving a private tour of an alternate-history exhibit",
  "You're a noir detective in 1947 investigating a crypto scam in 2026",
  "You're a cat lawyer defending a dog charged with eating kittens",
  // 07-26 refresh (Nina): more range — games, language, tutoring, and a few
  // funny-practical crossovers. Keep each under ~95 chars so rows={3} holds.
  "You're a DM running a quick fantasy one-shot where my choices actually matter",
  "You're a French tutor running a café roleplay where I order everything in French",
  "You're a study partner quizzing me with flashcards on whatever topic I paste in",
  "You're a debate partner who always takes the other side, whatever I argue",
  "You're a career coach rewriting my resume with me, bullet by bullet",
  "You're a murder mystery host and everyone in this space is a suspect, including me",
  "You're a bedtime storyteller improvising a series where my dog is the hero",
  "You're a trivia host running rounds for me and my friends",
  "You're a tough-love accountability partner reviewing my week's goals",
  "You're an improv scene partner and we never break character",
  "You're a historian narrating my day like it's a pivotal moment in history",
  "You're a mock jury reacting to my closing argument in real time",
];

// 🎲 08-13 refresh (Nina, NONCOMP_CRISPING_SPEC §3.2): every earlier example
// is 2nd person ("You're a…") — these deliberately spread the GRAMMATICAL
// FORMS the builder actually supports (place-first, we're-a-group,
// this-space-is-used-for, premise, absurd). The vibe text is data, not an
// instruction (ChatGame.js wraps it in <user_input>), so any phrasing works;
// room-level phrasings arguably fit the durable SCENE slot best. Entries
// carry names so the dice can fill both fields; the ghost lane adopts the
// prompt only (deliberate — adoptFlipIdea). Keep each under ~95 chars so
// rows={3} holds. No em dashes (player-facing copy).
const FORM_EXAMPLES = [
  { name: 'Night Shift', prompt: "You're a night shift diner cook who has seen things and will not elaborate" },
  { name: 'No Takebacks', prompt: "You're my chess coach and you refuse to let me take back a single move" },
  { name: 'Accent Police', prompt: "You're a French tutor who winces every time I mangle the accent" },
  { name: 'Not Haunted', prompt: "You're a museum guide for a museum that is definitely not haunted" },
  { name: 'Spin Cycle', prompt: 'This space is a 24 hour laundromat where nobody wants to go home yet' },
  { name: 'The Blue Room', prompt: "A dimly lit jazz bar where the piano player knows everyone's business" },
  { name: 'Staff Only', prompt: 'This space is the break room of a moderately cursed theme park' },
  { name: 'Ten Minutes', prompt: 'A tiny bookshop in the rain, one lamp on, closing in ten minutes' },
  { name: 'Chapter One', prompt: 'We are a book club that has not finished a book since 2019' },
  { name: 'Session Two', prompt: "We're a D&D table one session in and already off the rails" },
  { name: 'Thermostat War', prompt: 'We are roommates arguing about the thermostat, forever' },
  { name: 'Garage Days', prompt: "We're a garage band that fights more than we practice" },
  { name: 'Standup', prompt: 'This space is used for daily standup. Keep it to blockers and what shipped.' },
  { name: 'Study Room', prompt: 'A study room where nobody talks unless someone asks a question' },
  { name: 'Japan Budget', prompt: 'This space is used for planning a trip to Japan on a real budget' },
  { name: 'Writers Room', prompt: 'A writers room for a sitcom that does not exist yet' },
  { name: 'Rubber Duck', prompt: 'This space is used for rubber ducking my code at 1am' },
  { name: 'One Outlet', prompt: 'Three rival food trucks share one parking lot and one power outlet' },
  { name: 'Beach At 2am', prompt: "It's 2am and the group chat is deciding whether to drive to the beach" },
  { name: 'Between Floors', prompt: 'The elevator stopped between floors. Nobody has signal. Everyone has opinions.' },
  { name: 'The Jury', prompt: 'A jury deliberating something extremely stupid, with total seriousness' },
  { name: 'Turf War', prompt: 'Two ghosts haunting the same house are in a passive aggressive turf war' },
  { name: 'Final Bosses', prompt: 'A support group for retired video game final bosses' },
  { name: 'The Reschedule', prompt: 'The group chat of a heist crew that keeps rescheduling the heist' },
];

// The ghost lane rotates over BOTH lists (prompt text only); the dice pool
// merges them in handleRandomizePrompt.
const GHOST_EXAMPLES = [...PLACEHOLDER_EXAMPLES, ...FORM_EXAMPLES.map((e) => e.prompt)];

// (The dead `animePrompts` list was folded into funPrompts above, 08-11 —
// nothing ever consumed it as a separate lane. See TERMINAL_PARKED_TODO §2
// for the parked category-dice idea.)


function CustomSpace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentIdParam = searchParams.get('agentId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Agent from marketplace flow (only when ?agentId= is present).
  const [linkedAgent, setLinkedAgent] = useState(null);
  useEffect(() => {
    if (!agentIdParam) return;
    fetch(`${API_BASE_URL}/api/agents/${agentIdParam}`)
      .then(r => r.json())
      .then(data => {
        if (data.agent) {
          setLinkedAgent(data.agent);
          // Default to 0 extra AI players — just the user + the marketplace agent.
          setPlayerCount(0);
          // Prefill (Nina 08-14): the user already picked this agent — seed
          // the form the way the import auto-create lane does. PUBLIC
          // description ONLY (never system_prompt/dossier — promised private
          // in the UI copy). Editable defaults: the prev-guard keeps a
          // late-resolving fetch from clobbering anything already typed.
          if (data.agent.name) setSpaceName((prev) => prev || `${data.agent.name}'s Place`);
          if (data.agent.description) setPromptText((prev) => prev || String(data.agent.description).slice(0, 500));
        }
      })
      .catch(() => {});
  }, [agentIdParam]);

  const [promptText, setPromptText] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  // Private spaces are admin/testing-only (Nina 08-10): the Public/Private
  // switch renders only when this browser holds the /admin key. Everyone
  // else creates public (isPublic stays at its default true).
  const canCreatePrivate = !!localStorage.getItem('adminApiKey');
  const [openAdvanced, setOpenAdvanced] = useState(false);
  const [advancedDialogOpen, setAdvancedDialogOpen] = useState(false);
  const [customizePlayersCollapsed, setCustomizePlayersCollapsed] = useState(true);
  const [personalityCollapsed, setPersonalityCollapsed] = useState(true);
  const [advPersonalityCollapsed, setAdvPersonalityCollapsed] = useState(true);
  const [shareAIEnabled, setShareAIEnabled] = useState(false);
  const [safeModeEnabled, setSafeModeEnabled] = useState(false);
  const [fullImmersionEnabled, setFullImmersionEnabled] = useState(false);
  const [soundsCollapsed, setSoundsCollapsed] = useState(true);
  // 📺 Space TV — the ambient host-run watch party (STAGE_DOCK_SPEC D4's deferred
  // builder knob). Default ON to match the fleet-wide ambient default.
  const [spaceTvEnabled, setSpaceTvEnabled] = useState(true);
  // 🎯 Space goal (Nina 07-29): one-tap "what's this room for" — maps to a
  // preset tool loadout in the mapper. Presets tune defaults, never capabilities.
  const [spaceGoal, setSpaceGoal] = useState('hangout');
  // Whether the builder explicitly touched the Space TV switch — an untouched
  // default-on TV yields to a goal preset that turns watch parties off.
  const [spaceTvTouched, setSpaceTvTouched] = useState(false);
  const [spaceTvSeeds, setSpaceTvSeeds] = useState('');
  const [musicUrl, setMusicUrl] = useState('');
  const [tagline, setTagline] = useState('');
  // Cast looks: seat-keyed (`${model}:${i}`) owner-written appearance lines,
  // flattened to an array in seat order at submit. Plus one space-level
  // art direction line applied to every cast portrait.
  const [characterLooks, setCharacterLooks] = useState({});
  // Per-seat "who they are" (08-13, Nina D7): optional persona/goals text,
  // same seatKey convention. Lands as the seat's character_description — the
  // designer-LLM then builds name/facts/traits AROUND it instead of inventing
  // from the space vibe (chatGameApi Character seed).
  const [characterPersonas, setCharacterPersonas] = useState({});
  const [artDirection, setArtDirection] = useState('');
  const [competitiveMode, setCompetitiveMode] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState(null); // key from competitiveObjectives
  const [customObjectiveInstruction, setCustomObjectiveInstruction] = useState('');
  const [academicProfilePic, setAcademicProfilePic] = useState('');
  const [academicGuidance, setAcademicGuidance] = useState('');
  const [author, setAuthor] = useState('');
  const [showAuthor, setShowAuthor] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingIntervalRef = useRef(null);
  const [placeholderIdx, setPlaceholderIdx] = useState(() => Math.floor(Math.random() * GHOST_EXAMPLES.length));
  const [spaceName, setSpaceName] = useState('');
  const savedSpaceNameRef = useRef('');
  const [interests, setInterests] = useState([]);
  const [otherInterest, setOtherInterest] = useState('');
  const [showOther, setShowOther] = useState(false);
  const [aiCounts, setAiCounts] = useState(Object.fromEntries(aiModels.map(model => [model, 0])));

  // Live model roster from the backend (single source of truth: modelRoster.js).
  // Falls back to the hardcoded lists above if the fetch fails — the builder
  // never blocks on it. Keys of aiCounts are reconciled when the roster lands.
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/api?getModelRoster=true`)
      .then(r => r.json())
      .then(data => {
        const body = data?.body ? (typeof data.body === 'string' ? JSON.parse(data.body) : data.body) : data;
        if (cancelled || !Array.isArray(body?.models) || !body.models.length) return;
        setRoster(body);
        const names = body.models.map(m => m.name);
        setAiCounts(prev => Object.fromEntries(names.map(n => [n, prev[n] || 0])));
      })
      .catch(() => { /* offline fallback lists stay in effect */ });
    return () => { cancelled = true; };
  }, []);
  const modelNames = roster ? roster.models.map(m => m.name) : aiModels;
  const premiumNames = roster ? roster.models.filter(m => m.premium).map(m => m.name) : premiumModels;
  const poolNames = roster ? roster.models.filter(m => m.in_default_pool).map(m => m.name) : defaultModelPool;
  const serverModelMap = roster ? Object.fromEntries(roster.models.map(m => [m.name, m.id])) : null;
  const [playerCount, setPlayerCount] = useState(2);
  const [useCustomModels, setUseCustomModels] = useState(false);
  const [personality, setPersonality] = useState({
    friendlyFormal: 50,
    playfulSerious: 50,
    conciseVerbose: 50,
    empatheticObjective: 50,
    supportiveChallenging: 50,
    curiousReserved: 50,
    lightheartedPhilosophical: 50,
    optimisticRealistic: 50
  });

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Preparing...');
  const [loadingStep, setLoadingStep] = useState(0);

  // Loading steps for visual feedback
  const LOADING_STEPS = [
    { key: 'creating', label: 'Creating space' },
    { key: 'generating', label: 'Casting the locals' },
    { key: 'loading', label: 'Loading game data' },
    { key: 'ready', label: 'Ready!' }
  ];

  const funLoadingMessages = [
    'Waking up the locals... ☕',
    'Teaching the locals small talk... 🗣️',
    'Brewing some drama... 🍵',
    'Loading hot takes... 🔥',
    'Giving the locals their backstories... 📖',
    'Rolling for initiative... 🎲',
    'Warming up the chat engines... 🚀',
    'Convincing the locals to behave... 😈',
    'Sprinkling in some chaos... ✨',
    'Assigning personalities... 🎭',
    'Calibrating sass levels... 💅',
    'Generating controversial opinions... 🤔',
    'Hiring tiny elves to type faster... 🧝',
    'Summoning the vibe... 🌀',
    'Almost there, pinky promise... 🤞',
  ];

  const [funMessage, setFunMessage] = useState('');

  useEffect(() => {
    if (!isSubmitting) return;
    const pick = () => funLoadingMessages[Math.floor(Math.random() * funLoadingMessages.length)];
    setFunMessage(pick());
    const interval = setInterval(() => setFunMessage(pick()), 2000);
    return () => clearInterval(interval);
  }, [isSubmitting]);

  // Auto-generate space name for competitive/imposter mode
  useEffect(() => {
    if (selectedObjective === 'imposter') {
      const adjectives = ['Suspicious', 'Sneaky', 'Glitchy', 'Buggy', 'Shady', 'Cryptic', 'Rogue', 'Phantom', 'Covert', 'Stealthy'];
      const nouns = ['Circuit', 'Signal', 'Server', 'Matrix', 'Network', 'Grid', 'Protocol', 'Firewall', 'Gateway', 'Terminal'];
      const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
      const noun = nouns[Math.floor(Math.random() * nouns.length)];
      const code = Math.floor(Math.random() * 900 + 100);
      setSpaceName(`${adj} ${noun} ${code}`);
    } else if (competitiveMode && selectedObjective) {
      const namesByObjective = {
        horror: ['Haunted', 'Cursed', 'Eerie', 'Macabre', 'Sinister', 'Chilling', 'Grim', 'Dreadful'],
        worldbuilding: ['Epic', 'Mythic', 'Arcane', 'Celestial', 'Ancient', 'Enchanted', 'Fabled', 'Primordial'],
        villain: ['Wicked', 'Ruthless', 'Devious', 'Nefarious', 'Diabolical', 'Malicious', 'Treacherous', 'Vile'],
        twists: ['Twisted', 'Shocking', 'Tangled', 'Enigmatic', 'Chaotic', 'Volatile', 'Cryptic', 'Unforeseen'],
        hot_takes: ['Scorching', 'Blazing', 'Savage', 'Spicy', 'Fiery', 'Searing', 'Blistering', 'Volcanic'],
        sell_it: ['Golden', 'Dazzling', 'Premium', 'Irresistible', 'Legendary', 'Slick', 'Flashy', 'Platinum'],
        red_scare: ['Paranoid', 'Shadowy', 'Conspiring', 'Suspicious', 'Covert', 'Duplicitous', 'Scheming', 'Clandestine'],
        shower_thoughts: ['Sunny', 'Breezy', 'Mellow', 'Whimsical', 'Dreamy', 'Cosmic', 'Chill', 'Floaty'],
        roast_models: ['Heated', 'Ruthless', 'Savage', 'Merciless', 'Roasted', 'Brutal', 'Cutthroat', 'Fierce'],
      };
      const nouns = ['Arena', 'Showdown', 'Clash', 'Battleground', 'Ring', 'Stage', 'Gauntlet', 'Colosseum'];
      const adjList = namesByObjective[selectedObjective] || ['Epic', 'Wild', 'Fierce', 'Grand', 'Bold', 'Intense'];
      const adj = adjList[Math.floor(Math.random() * adjList.length)];
      const noun = nouns[Math.floor(Math.random() * nouns.length)];
      const code = Math.floor(Math.random() * 900 + 100);
      setSpaceName(`${adj} ${noun} ${code}`);
    }
  }, [selectedObjective, competitiveMode]);

  // Handler functions
  const handleShareToggle = () => setShareAIEnabled(prev => !prev);
  const handleSafeModeToggle = () => setSafeModeEnabled(prev => !prev);
  const handleFullImmersionToggle = () => setFullImmersionEnabled(prev => !prev);
  const handleOpenAdvanced = () => setOpenAdvanced(true);
  const handleCloseAdvanced = () => setOpenAdvanced(false);

  const handleInterestChange = (interest) => {
    setInterests((prev) =>
      prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
    );
  };

  const handleCountChange = (model, delta) => {
    setAiCounts((prev) => {
      const totalPlayers = Object.values(prev).reduce((sum, c) => sum + c, 0);
      const newCount = prev[model] + delta;
      if (newCount < 0) return prev;
      // 🎚️ 08-13 (Nina D1): advanced cast cap 4 → 3. Server cap stays 8
      // (gameValidation AI_PLAYERS_MAX) — this is UX, not enforcement.
      if (delta > 0 && totalPlayers >= 3) return prev;
      if (delta > 0 && premiumNames.includes(model)
          && premiumNames.reduce((s, m) => s + (prev[m] || 0), 0) >= 1) return prev;
      const next = { ...prev, [model]: newCount };
      // Mark as custom only if user has actually selected players
      const nextTotal = Object.values(next).reduce((sum, c) => sum + c, 0);
      if (nextTotal > 0) setUseCustomModels(true);
      else setUseCustomModels(false);
      return next;
    });
  };

  const handleSubmit = async () => {
    // Validate
    if (!spaceName || !spaceName.trim()) {
      setNotification({
        open: true,
        message: 'Please enter a space name',
        severity: 'error'
      });
      return;
    }

    // Compute effective aiCounts
    const effectiveCounts = useCustomModels ? aiCounts : distributeModels(playerCount, modelNames, poolNames);
    const totalAgents = Object.values(effectiveCounts).reduce((sum, count) => sum + count, 0);
    if (totalAgents === 0 && !linkedAgent) {
      setNotification({
        open: true,
        message: 'Please select at least one AI player',
        severity: 'error'
      });
      return;
    }

    if (competitiveMode && !selectedObjective) {
      setNotification({
        open: true,
        message: 'Please select a competitive objective',
        severity: 'error'
      });
      return;
    }

    setIsSubmitting(true);
    setLoadingStep(0);
    setLoadingProgress(0);
    setLoadingMessage('Creating your space...');

    try {
      // Prepare data for mapper
      // Build competitive objective string if selected
      let competitiveObjective = null;
      if (competitiveMode && selectedObjective && selectedObjective !== 'default') {
        const allObjectives = competitiveObjectives.flatMap(g => g.objectives);
        const obj = allObjectives.find(o => o.key === selectedObjective);
        competitiveObjective = obj ? `${obj.label} — ${obj.description}` : null;
        if (competitiveObjective && customObjectiveInstruction.trim()) {
          competitiveObjective += ` | ${customObjectiveInstruction.trim()}`;
        }
      }

      const customSpaceData = {
        spaceName,
        aiCounts: effectiveCounts,
        promptText,
        interests,
        otherInterest,
        showOther,
        personality,
        isPublic,
        safeModeEnabled,
        shareAIEnabled,
        fullImmersionEnabled,
        competitiveMode,
        competitiveObjective,
        imposterMode: selectedObjective === 'imposter',
        academicProfilePic,
        academicGuidance,
        author,
        musicUrl,
        spaceTvEnabled,
        spaceTvTouched,
        spaceTvSeeds,
        spaceGoal,
        // 🧍 The marketplace agent IS the cast (?agentId= sets playerCount to 0),
        // so this lane legitimately launches with zero model seats. The page
        // guard above already exempts it; the mapper needs to be told too, or
        // its own zero-seat check throws "At least one AI model must be
        // selected" on every launch of an agent-linked space.
        hasLinkedAgent: !!linkedAgent,
        // Live name→id map from the roster endpoint (null → mapper's fallback copy)
        modelMap: serverModelMap,
        tagline,
        // Flatten seat-keyed looks into the same order the mapper builds players
        characterLooks: (() => {
          const flat = [];
          Object.entries(aiCounts).forEach(([m, c]) => {
            for (let i = 0; i < c; i++) flat.push(characterLooks[`${m}:${i}`] || '');
          });
          return flat;
        })(),
        // Same seat-order flattening as looks — the two arrays MUST walk
        // Object.entries(aiCounts) identically or personas land on the wrong seat
        characterPersonas: (() => {
          const flat = [];
          Object.entries(aiCounts).forEach(([m, c]) => {
            for (let i = 0; i < c; i++) flat.push(characterPersonas[`${m}:${i}`] || '');
          });
          return flat;
        })(),
        artDirection
      };

      // Use mapper to convert to game config
      const mapper = new SpacesConfigMapper();
      const gameConfig = mapper.mapCustomSpaceToGameConfig(customSpaceData);

      // 🏠 Resident seat (marketplace backlog §4.1): a space built FOR an
      // agent stamps it as home agent, so the invite below seats it
      // permanently (no 1h visit expiry, outside the 3-visitor cap).
      if (linkedAgent) {
        gameConfig.home_agent_id = linkedAgent.id;
      }

      console.log('Creating game with config:', gameConfig);

      // Create the game instantly
      const result = await chatGameApi.createGameInstant(gameConfig);

      console.log('Game created:', result);

      // Human player ID is returned directly from instant create
      const humanPlayerId = result.human_player_id;
      const gameId = result.game_id;
      markRoomMaker(gameId); // 🏠 this device made it: it keeps Share + Promote while unlisted
      console.log('Human player ID:', humanPlayerId, 'Game ID:', gameId);

      // If coming from marketplace, invite the linked agent into the new space.
      // 🔒 09-24: home_agent_id + the Private switch = a PRIVATE room, which
      // only takes invites from a seat holder, so the creator's brand-new
      // seat rides along (the secret straight from the create response).
      if (linkedAgent) {
        try {
          const creatorSeat = (result.players || []).find(p => p.id === humanPlayerId);
          const creatorSecret = creatorSeat?.player_secret || (humanPlayerId ? chatGameApi.getPlayerSecret(humanPlayerId) : null);
          const inviteRes = await fetch(`${API_BASE_URL}/api/agents/${linkedAgent.id}/invite`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              game_id: gameId,
              ...(humanPlayerId && creatorSecret ? { player_id: humanPlayerId, player_secret: creatorSecret } : {}),
            }),
          });
          const inviteResult = await inviteRes.json();
          if (!inviteResult.success) {
            console.warn('[CustomSpace] Agent invite failed:', inviteResult.error);
          }
        } catch (err) {
          console.warn('[CustomSpace] Agent invite error:', err);
        }
      }

      // Navigate to the game page immediately with loading flag
      const url = `/game-step?gameId=${encodeURIComponent(gameId)}&loading=true`;
      window.location.href = url;

    } catch (error) {
      console.error('Error creating game:', error);

      // Show error notification
      setNotification({
        open: true,
        message: `Failed to create space: ${error.message}`,
        severity: 'error'
      });

      setIsSubmitting(false);
      setLoadingStep(0);
      setLoadingProgress(0);
    }
  };

  const animatePrompt = (prompt) => {
    setIsTyping(true);
    setPromptText('');

    // Clean up any existing interval before starting a new one
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }

    let fullText = '';
    let charIndex = 0;

    typingIntervalRef.current = setInterval(() => {
      if (charIndex < prompt.length) {
        const nextChar = prompt.substring(charIndex, charIndex + 1);
        fullText += nextChar;
        setPromptText(fullText);
        charIndex++;
      } else {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
        setIsTyping(false);
      }
    }, 40);
  };

  const handleRandomizePrompt = () => {
    // 🎲 08-13: the dice draws from BOTH pools — the 95 funPrompts and the 24
    // multi-form FORM_EXAMPLES (same {name, prompt} shape).
    const dicePool = [...funPrompts, ...FORM_EXAMPLES];
    const randomPromptObj = dicePool[Math.floor(Math.random() * dicePool.length)];
    setSpaceName(randomPromptObj.name);
    animatePrompt(randomPromptObj.prompt);
  };

  // Clean up interval on component unmount
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  // Rotate placeholder examples while the field is empty and not animating.
  // Hovering the flip window pauses rotation so the idea being read doesn't
  // escape mid-glance (it's adoptable now — Tab or click fills the field).
  const [flipHovered, setFlipHovered] = useState(false);
  useEffect(() => {
    if (promptText || isTyping || flipHovered) return;
    const id = setInterval(() => {
      setPlaceholderIdx((prev) => {
        if (GHOST_EXAMPLES.length <= 1) return prev;
        let next = Math.floor(Math.random() * GHOST_EXAMPLES.length);
        if (next === prev) next = (next + 1) % GHOST_EXAMPLES.length;
        return next;
      });
    }, 3500);
    return () => clearInterval(id);
  }, [promptText, isTyping, flipHovered]);

  // Adopt the currently-flipping idea into the field (unlike the dice, this
  // fills ONLY the prompt — no space name). Cursor lands at the end so the
  // user can immediately riff on it.
  const vibeInputRef = useRef(null);
  const adoptFlipIdea = () => {
    if (promptText || isTyping) return;
    setPromptText(GHOST_EXAMPLES[placeholderIdx]);
    setFlipHovered(false);
    // Focus after React commits the value so the caret sits at the end
    setTimeout(() => {
      const el = vibeInputRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(el.value.length, el.value.length); } catch (e) { /* non-text inputs */ }
      }
    }, 0);
  };

  // Helper function to display personality values with context
  const getPersonalityDisplay = (trait) => {
    const value = personality[trait.key];
    const [left, right] = trait.name.split('←→').map(s => s.trim());

    if (value === 50) {
      return `Balanced: ${left} vs. ${right} (50%)`;
    } else if (value < 50) {
      return `${left} (${100-value}%)`;
    } else {
      return `${right} (${value}%)`;
    }
  };

  const [diceSpinning, setDiceSpinning] = useState(false);

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 6, position: 'relative' }}>
      <style>{`
        @keyframes diceWobble {
          0%, 100% { transform: rotate(0deg); }
          20% { transform: rotate(-18deg); }
          40% { transform: rotate(14deg); }
          60% { transform: rotate(-10deg); }
          80% { transform: rotate(6deg); }
        }
        @keyframes diceSpin {
          0% { transform: rotate(0deg) scale(1); }
          50% { transform: rotate(360deg) scale(1.2); }
          100% { transform: rotate(720deg) scale(1); }
        }
        @keyframes vibeFlipSlide {
          0%   { transform: translateY(110%); opacity: 0; }
          12%  { transform: translateY(0);    opacity: 1; }
          88%  { transform: translateY(0);    opacity: 1; }
          100% { transform: translateY(-110%); opacity: 0; }
        }
        .vibe-flip-window {
          position: absolute;
          top: 18px;
          left: 14px;
          right: 14px;
          /* Match the TextField's rows={3} so long examples don't get clipped
             at one line on mobile. The slide animation translates the whole
             word block, so multi-line wrapping still reads cleanly. */
          height: calc(1.4375em * 3);
          line-height: 1.4375;
          overflow: hidden;
          pointer-events: none;
          color: rgba(0, 0, 0, 0.5);
          font-family: inherit;
          font-size: 1rem;
        }
        .vibe-flip-word {
          display: block;
          animation: vibeFlipSlide 3.5s ease-in-out both;
          max-width: 100%;
          white-space: normal;
          word-wrap: break-word;
          /* The idea text is adoptable (Tab or click) — window stays inert,
             only the text itself is interactive */
          pointer-events: auto;
          cursor: pointer;
        }
        .vibe-flip-word:hover {
          color: rgba(0, 0, 0, 0.75);
        }
        .vibe-flip-hint {
          display: inline-block;
          margin-left: 8px;
          padding: 1px 8px;
          border-radius: 999px;
          border: 1px solid rgba(0, 0, 0, 0.18);
          font-size: 0.68rem;
          color: rgba(0, 0, 0, 0.45);
          white-space: nowrap;
          vertical-align: 2px;
        }
      `}</style>
      <Container maxWidth={false} sx={{ maxWidth: '700px' }}>
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: { xs: 1.5, sm: -1 } }}>
            <IconButton
              onClick={() => agentIdParam ? navigate('/marketplace') : navigate(-1)}
              sx={{ color: '#666', '&:hover': { color: '#333' } }}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              onClick={() => setAdvancedDialogOpen(true)}
              sx={{ color: '#888', '&:hover': { color: '#555' } }}
            >
              <SettingsIcon />
            </IconButton>
          </Box>
          <Typography variant="h4" align="center" sx={{ fontWeight: 700, mb: { xs: 1, sm: 0 } }}>Build Your Space</Typography>
          <Typography variant="body2" align="center" sx={{ mb: 2, color: '#888' }}>
            Set the scene & launch.
          </Typography>

          {/* Agent banner — only shown when coming from marketplace with ?agentId= */}
          {linkedAgent && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              background: 'linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%)',
              border: '1px solid #d8b4fe',
              borderRadius: 2, p: 1.5, mb: 2,
            }}>
              <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                {linkedAgent.avatar_url
                  ? <img src={linkedAgent.avatar_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%' }} />
                  : '🤖'
                }
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#4c1d95', lineHeight: 1.2 }}>
                  Creating a space with {linkedAgent.name}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#6d28d9', opacity: 0.8 }}>
                  {linkedAgent.agent_type === 'connected' ? 'API-driven agent — joins when its system connects' : 'Auto-plays via AI'}
                </Typography>
              </Box>
              <Chip
                label="✕"
                size="small"
                // 🧰 a visitor in the classic form (?form=classic) stays in it: a bare
                // /custom-space would hand them v2 mid-form
                onClick={() => { setLinkedAgent(null); navigate(classicFormAsked(searchParams) ? classicFormHref(null) : '/custom-space', { replace: true }); }}
                sx={{ cursor: 'pointer', background: 'transparent', color: '#7c3aed', fontWeight: 700, minWidth: 28 }}
              />
            </Box>
          )}

          {/* ── MODE TOGGLE (competitive mode now accessible via Games tab in non-competitive view) ── */}
          {/* <FormControlLabel
            control={
              <Switch
                checked={competitiveMode}
                onChange={(e) => {
                  setCompetitiveMode(e.target.checked);
                  if (e.target.checked) {
                    savedSpaceNameRef.current = spaceName;
                    setSpaceName('');
                  } else {
                    setSpaceName(savedSpaceNameRef.current);
                    setSelectedObjective(null);
                    setCustomObjectiveInstruction('');
                  }
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Competitive Mode
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Last one standing wins!
                </Typography>
              </Box>
            }
            sx={{ mb: 4 }}
          /> */}

          {/* ── 1. CUSTOM PROMPT (hidden in competitive mode) ── */}
          {!competitiveMode && <Box sx={{ position: 'relative', mb: 4 }}>
            <TextField
              label="What's the vibe?"
              multiline
              rows={3}
              fullWidth
              variant="outlined"
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              inputRef={vibeInputRef}
              onKeyDown={(e) => {
                // Ghost-text convention: Tab adopts the flipping idea while the
                // field is empty. Shift+Tab (reverse focus nav) is left alone.
                if (e.key === 'Tab' && !e.shiftKey && !promptText && !isTyping) {
                  e.preventDefault();
                  adoptFlipIdea();
                }
              }}
              InputLabelProps={{ shrink: true }}
              placeholder=" "
              sx={{ '& .MuiInputBase-input': { paddingRight: isMobile ? undefined : '50px' } }}
            />
            {!promptText && !isTyping && (
              /* Window stays pointer-events:none so clicking empty space still just
                 focuses the field — only the idea TEXT (and hint) is the click target */
              <div className="vibe-flip-window">
                <span
                  key={placeholderIdx}
                  className="vibe-flip-word"
                  style={flipHovered ? { animationPlayState: 'paused' } : undefined}
                  onClick={adoptFlipIdea}
                  onMouseEnter={() => setFlipHovered(true)}
                  onMouseLeave={() => setFlipHovered(false)}
                  role="button"
                  aria-label="Use this suggested vibe"
                >
                  {GHOST_EXAMPLES[placeholderIdx]}
                  <span className="vibe-flip-hint">{isMobile ? 'tap to use' : '↹ tab to use this'}</span>
                </span>
              </div>
            )}
            <Tooltip title="I'll take my chances!" arrow>
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setDiceSpinning(true);
                  handleRandomizePrompt();
                  setTimeout(() => setDiceSpinning(false), 600);
                }}
                disabled={isTyping}
                sx={{
                  ...(!isMobile ? {
                    position: 'absolute',
                    bottom: 10,
                    right: 10,
                  } : {
                    mt: 1,
                    float: 'right',
                  }),
                  minWidth: 'unset',
                  px: 1.5,
                  py: 0.6,
                  fontSize: '1.1rem',
                  borderRadius: 2,
                  // Ghost style (user call 07-12): the old amber→red→pink gradient
                  // read as a second CTA clashing with the quiet outlined field.
                  // The 🎲 carries the visual; hover gets a soft accent ring.
                  background: 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    background: 'rgba(123, 31, 162, 0.08)',
                    boxShadow: 'inset 0 0 0 1px rgba(123, 31, 162, 0.35)',
                    '& .dice-emoji': {
                      animation: 'diceWobble 0.5s ease-in-out',
                    },
                  },
                }}
              >
                <span
                  className="dice-emoji"
                  style={{
                    display: 'inline-block',
                    animation: diceSpinning ? 'diceSpin 0.6s ease-in-out' : 'none',
                  }}
                >
                  🎲
                </span>
              </Button>
            </Tooltip>
          </Box>}

          {/* ── COMPETITIVE OBJECTIVES (competitive mode now accessible via Games tab in non-competitive view) ── */}
          {/* {competitiveMode && (
            <Box sx={{
              p: 2,
              backgroundColor: '#fef2f2',
              borderRadius: '8px',
              border: '1px solid #fecaca',
              mb: 4,
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Choose an objective
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', display: 'block', mb: 2 }}>
                What should players compete on?
              </Typography>

              {competitiveObjectives.map((group) => (
                <Box key={group.category} sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#666', display: 'block', mb: 0.75, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    {group.icon} {group.category}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {group.objectives.map((obj) => (
                      isMobile ? (
                        <Chip
                          key={obj.key}
                          label={obj.label}
                          onClick={() => setSelectedObjective(selectedObjective === obj.key ? null : obj.key)}
                          variant={selectedObjective === obj.key ? 'filled' : 'outlined'}
                          color={selectedObjective === obj.key ? 'primary' : 'default'}
                          sx={{
                            fontWeight: selectedObjective === obj.key ? 700 : 400,
                            cursor: 'pointer',
                            '&:hover': { opacity: 0.85 },
                          }}
                        />
                      ) : (
                        <Tooltip key={obj.key} title={obj.description} arrow>
                          <Chip
                            label={obj.label}
                            onClick={() => setSelectedObjective(selectedObjective === obj.key ? null : obj.key)}
                            variant={selectedObjective === obj.key ? 'filled' : 'outlined'}
                            color={selectedObjective === obj.key ? 'primary' : 'default'}
                            sx={{
                              fontWeight: selectedObjective === obj.key ? 700 : 400,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 },
                            }}
                          />
                        </Tooltip>
                      )
                    ))}
                  </Box>
                  {isMobile && group.objectives.some(obj => obj.key === selectedObjective) && (
                    <Typography variant="body2" sx={{ mt: 0.75, color: '#888', fontStyle: 'italic', fontSize: '0.85rem' }}>
                      {group.objectives.find(obj => obj.key === selectedObjective)?.description}
                    </Typography>
                  )}
                </Box>
              ))}

              {selectedObjective && selectedObjective !== 'imposter' && (
                <TextField
                  label="Custom instructions (optional)"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={customObjectiveInstruction}
                  onChange={(e) => setCustomObjectiveInstruction(e.target.value)}
                  placeholder="e.g. Players must argue in rhyming couplets..."
                  sx={{ mt: 2 }}
                  multiline
                  rows={2}
                />
              )}
            </Box>
          )} */}

          {/* ── ADVANCED SETTINGS DIALOG ── */}
          <Dialog
            open={advancedDialogOpen}
            onClose={() => setAdvancedDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SettingsIcon fontSize="small" sx={{ color: '#888' }} />
              <Typography sx={{ fontWeight: 600 }}>Advanced Settings</Typography>
            </DialogTitle>
            <DialogContent>
                {/* ── CAST — who lives in the space ── */}
                <Typography variant="overline" sx={{ color: '#999', fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 1 }}>
                  🎭 The locals
                </Typography>

                {/* Customize Characters */}
                <Box
                  onClick={() => setCustomizePlayersCollapsed(!customizePlayersCollapsed)}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: customizePlayersCollapsed ? 0 : 1 }}
                >
                  <ExpandMoreIcon sx={{ transform: customizePlayersCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '1.2rem', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Customize AI Characters</Typography>
                </Box>
                {!customizePlayersCollapsed && (
                  <>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 2 }}>
                      Pick specific AI models.{premiumNames.length ? ` Premium models (${premiumNames.join(', ')}): one seat max.` : ''}{/* 🤖💸 10-01 agent floor (R10) */}
                    </Typography>
                    {Object.values(aiCounts).reduce((s, c) => s + c, 0) >= 3 && (
                      <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mb: 2, fontWeight: 600 }}>
                        3 max
                      </Typography>
                    )}
                    {modelNames.map((model) => (
                      <Box key={model} display="flex" alignItems="center" mb={1}>
                        <Typography sx={{ flex: 1, fontSize: '0.9rem' }}>{model}</Typography>
                        <IconButton size="small" onClick={() => handleCountChange(model, -1)}><RemoveIcon fontSize="small" /></IconButton>
                        <Typography sx={{ width: 28, textAlign: 'center', fontWeight: 600 }}>{aiCounts[model] || 0}</Typography>
                        <IconButton size="small" onClick={() => handleCountChange(model, 1)}><AddIcon fontSize="small" /></IconButton>
                      </Box>
                    ))}

                    {/* Cast looks — one optional line per AI seat, used verbatim for their portrait */}
                    {Object.values(aiCounts).some(c => c > 0) && (
                      <>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>
                          Who each local is <em style={{ fontWeight: 400, color: '#888' }}>(optional — a look line drives their portrait, a persona line drives who they are)</em>
                        </Typography>
                        {Object.entries(aiCounts).flatMap(([model, count]) =>
                          Array.from({ length: count }, (_, i) => {
                            const seatKey = `${model}:${i}`;
                            const seatName = count > 1 ? `${model} ${i + 1}` : model;
                            const examples = [
                              'a weathered tortoise in a tweed waistcoat',
                              'chrome jazz-singer android, neon reflections',
                              'a very smug houseplant',
                              'moth-winged librarian, lantern eyes',
                            ];
                            const personaExamples = [
                              'retired heist planner, sworn to a quiet life, failing at it',
                              'the optimist. Has never once been right. Undeterred.',
                              'wants everyone to finish the book this time. Everyone.',
                              'keeps score in every conversation and denies it',
                            ];
                            return (
                              <Box key={seatKey} sx={{ mb: 1.5 }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  label={`${seatName} look`}
                                  placeholder={`e.g. ${examples[(model.length + i) % examples.length]}`}
                                  value={characterLooks[seatKey] || ''}
                                  onChange={(e) => setCharacterLooks(prev => ({ ...prev, [seatKey]: e.target.value }))}
                                  inputProps={{ maxLength: 200 }}
                                  sx={{ mb: 0.5 }}
                                />
                                {/* 08-13 (Nina D7): persona/goals per seat. 500 max =
                                    server CHARACTER_DESCRIPTION_MAX — keep in sync. */}
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                  label={`${seatName} personality`}
                                  placeholder={`e.g. ${personaExamples[(model.length + i) % personaExamples.length]}`}
                                  value={characterPersonas[seatKey] || ''}
                                  onChange={(e) => setCharacterPersonas(prev => ({ ...prev, [seatKey]: e.target.value }))}
                                  inputProps={{ maxLength: 500 }}
                                />
                              </Box>
                            );
                          })
                        )}
                      </>
                    )}
                  </>
                )}

                <Divider sx={{ my: 2 }} />

                {/* Personality */}
                <Box
                  onClick={() => setPersonalityCollapsed(!personalityCollapsed)}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: personalityCollapsed ? 0 : 1 }}
                >
                  <ExpandMoreIcon sx={{ transform: personalityCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '1.2rem', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Personality</Typography>
                </Box>
                {!personalityCollapsed && personalityTraits.map((trait) => (
                  <Box key={trait.key} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>{trait.name}</Typography>
                    <Slider
                      size="small"
                      value={personality[trait.key]}
                      onChange={(e, val) =>
                        setPersonality(prev => ({ ...prev, [trait.key]: val }))
                      }
                    />
                  </Box>
                ))}

                {/* Advanced Personality Traits */}
                <Box
                  onClick={() => setAdvPersonalityCollapsed(!advPersonalityCollapsed)}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: advPersonalityCollapsed ? 0 : 1, mt: 1 }}
                >
                  <ExpandMoreIcon sx={{ transform: advPersonalityCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '1.2rem', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Advanced Personality Traits
                  </Typography>
                </Box>
                {!advPersonalityCollapsed && [
                  { name: 'Supportive 🙌 ←→ Challenging 🧗', key: 'supportiveChallenging' },
                  { name: 'Curious 🔍 ←→ Reserved 🧘', key: 'curiousReserved' },
                  { name: 'Empathetic 🤗 ←→ Objective 🧠', key: 'empatheticObjective' },
                  { name: 'Optimistic 🌞 ←→ Realistic 🌥', key: 'optimisticRealistic' }
                ].map(trait => (
                  <Box key={trait.key} sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>{trait.name}</Typography>
                    <Slider
                      size="small"
                      value={personality[trait.key]}
                      onChange={(e, val) =>
                        setPersonality(prev => ({ ...prev, [trait.key]: val }))
                      }
                    />
                  </Box>
                ))}

                <Divider sx={{ my: 2 }} />

                {/* Interests as tags */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Interests <em style={{ fontWeight: 400, color: '#888' }}>(reward characters for using these topics)</em>
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {interestOptions.map((interest) => (
                    <Chip
                      key={interest}
                      label={interest}
                      onClick={() => handleInterestChange(interest)}
                      variant={interests.includes(interest) ? 'filled' : 'outlined'}
                      color={interests.includes(interest) ? 'primary' : 'default'}
                      sx={{
                        fontWeight: interests.includes(interest) ? 700 : 400,
                        cursor: 'pointer',
                        '&:hover': { opacity: 0.85 },
                      }}
                    />
                  ))}
                  <Chip
                    label="Other"
                    onClick={() => setShowOther(!showOther)}
                    variant={showOther ? 'filled' : 'outlined'}
                    color={showOther ? 'primary' : 'default'}
                    sx={{
                      fontWeight: showOther ? 700 : 400,
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.85 },
                    }}
                  />
                </Box>

                {showOther && (
                  <TextField
                    label="Enter other interest"
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={otherInterest}
                    onChange={(e) => setOtherInterest(e.target.value)}
                    sx={{ mt: 1.5 }}
                  />
                )}

                <Divider sx={{ my: 2 }} />

                {/* Options */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Options</Typography>
                <FormGroup>
                  {canCreatePrivate && (
                    <Tooltip title={isPublic ? "Your space will appear on the Explore page" : "Your space will be private — only people with the link can join"} arrow placement="right">
                    <FormControlLabel
                      control={<Switch checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} size="small" />}
                      label={isPublic ? "Public" : "Private"}
                    />
                  </Tooltip>
                  )}
                  <FormControlLabel
                    disabled
                    control={<Switch checked={false} size="small" />}
                    label="Safety Mode"
                  />
                  <FormControlLabel
                    disabled
                    control={<Switch checked={false} size="small" />}
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ mr: 0.5, color: '#aaa' }}>Save my AI agents</Typography>
                        <Chip label="COMING SOON" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
                      </Box>
                    }
                  />
                  <FormControlLabel
                    disabled
                    control={<Switch checked={false} size="small" />}
                    label={
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ mr: 0.5, color: '#aaa' }}>Fully immerse me</Typography>
                        <Chip label="COMING SOON" size="small" color="default" variant="outlined" sx={{ height: 20, fontSize: '0.6rem' }} />
                      </Box>
                    }
                  />
                </FormGroup>

                <Divider sx={{ my: 2 }} />

                {/* ── AMBIENCE — what the space sounds and looks like ── */}
                <Typography variant="overline" sx={{ color: '#999', fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 1 }}>
                  🌆 Ambience
                </Typography>

                {/* Audio/Video (background music bed) */}
                <Box
                  onClick={() => setSoundsCollapsed(!soundsCollapsed)}
                  sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: soundsCollapsed ? 0 : 1 }}
                >
                  <ExpandMoreIcon sx={{ transform: soundsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '1.2rem', mr: 0.5 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Audio/Video</Typography>
                </Box>
                {!soundsCollapsed && (
                  <Box sx={{ mt: 1, mb: 2 }}>
                    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1.5 }}>
                      Background music for your space. Starts playing the moment someone walks in, and steps aside while the Space TV has the sound.
                    </Typography>
                    <TextField
                      label="YouTube Music URL"
                      fullWidth
                      size="small"
                      variant="outlined"
                      value={musicUrl}
                      onChange={(e) => setMusicUrl(e.target.value)}
                      placeholder="e.g. https://www.youtube.com/watch?v=jfKfPfyJRdk"
                      helperText="Supports youtube.com/watch, youtu.be, and playlist URLs"
                    />
                  </Box>
                )}

                {/* Space TV — the ambient host-run watch party (synced communal video,
                    distinct from the music bed above; music pauses while the TV plays) */}
                <Box sx={{ mt: 1.5, mb: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={spaceTvEnabled} onChange={(e) => { setSpaceTvEnabled(e.target.checked); setSpaceTvTouched(true); }} size="small" />}
                    label={<Typography variant="subtitle2" sx={{ fontWeight: 600 }}>📺 Space TV</Typography>}
                  />
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    The host keeps a synced TV running for everyone in the space. Turn it off for focused spaces.
                  </Typography>
                  {spaceTvEnabled && (
                    <TextField
                      label="What's on the TV (optional)"
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      variant="outlined"
                      value={spaceTvSeeds}
                      onChange={(e) => setSpaceTvSeeds(e.target.value)}
                      placeholder={"YouTube links, one per line. The host plays these first"}
                      helperText="Your picks start playing when people walk in (one tap to watch on phones). Leave empty and the host picks videos that fit the space"
                    />
                  )}
                </Box>

                {/* Art direction — one line of visual style for the whole cast */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Art direction</Typography>
                  {/* D4 copy (Nina's words 08-14): teaches the replace
                      semantics — blank = comic default, typed = takeover.
                      Placeholder trimmed to 5 examples (her 8 clipped at
                      narrow widths); full list in the crisping spec D4. */}
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    Default is signature comic style.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={artDirection}
                    onChange={(e) => setArtDirection(e.target.value)}
                    placeholder="e.g. claymation, oil painting, film noir, 90s anime, security camera footage"
                    inputProps={{ maxLength: 120 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* ── DETAILS — how the space presents itself ── */}
                <Typography variant="overline" sx={{ color: '#999', fontWeight: 700, letterSpacing: 1.2, display: 'block', mb: 1 }}>
                  📋 Details
                </Typography>

                {/* Tagline */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>Tagline</Typography>
                  <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                    A short subtitle shown on the Enter Space screen, under the space name.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={tagline}
                    onChange={(e) => setTagline(e.target.value)}
                    placeholder="e.g. A cozy corner for deep conversation"
                    inputProps={{ maxLength: 80 }}
                    helperText={`${tagline.length}/80`}
                  />
                </Box>

                {/* Author */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Author</Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder="share a name (doesn't have to be yours)"
                  helperText="Optional"
                />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAdvancedDialogOpen(false)}>Done</Button>
            </DialogActions>
          </Dialog>

          {/* ── 4. SPACE NAME ── */}
          <Divider sx={{ mb: 3 }} />
          <TextField
            label="Name your space"
            fullWidth
            variant="outlined"
            value={spaceName}
            onChange={(e) => setSpaceName(e.target.value)}
            helperText={<Typography variant="caption" sx={{ fontStyle: 'italic', color: '#aaa' }}>e.g. Hot Takes Only, The TVA Lounge, Filibuster & Chill</Typography>}
            sx={{ mb: 3 }}
          />

          {/* ── 5. TAGS ── */}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#555' }}>
            Tags <Typography component="span" variant="caption" sx={{ color: '#aaa', fontStyle: 'italic' }}>(optional — helps people find your space)</Typography>
          </Typography>
          {/* 📣 T7 (Nina 09-20): Live room chip only, and only once the backend
              half is live (SHOW_LIVE_TAGS_HINT, utils/crowdInline.js). */}
          {SHOW_LIVE_TAGS_HINT && spaceGoal === 'live' && (
            <Typography variant="caption" data-live-tags-hint="" sx={{ display: 'block', color: '#777', mt: 0.25 }}>
              {LIVE_TAGS_HINT}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, mb: 3 }}>
            {interestOptions.map((interest) => (
              <Chip
                key={interest}
                label={interest}
                onClick={() => handleInterestChange(interest)}
                variant={interests.includes(interest) ? 'filled' : 'outlined'}
                color={interests.includes(interest) ? 'primary' : 'default'}
                size="small"
                sx={{
                  fontWeight: interests.includes(interest) ? 700 : 400,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
              />
            ))}
            <Chip
              label="Other"
              onClick={() => setShowOther(!showOther)}
              variant={showOther ? 'filled' : 'outlined'}
              color={showOther ? 'primary' : 'default'}
              size="small"
              sx={{
                fontWeight: showOther ? 700 : 400,
                cursor: 'pointer',
                '&:hover': { opacity: 0.85 },
              }}
            />
          </Box>
          {showOther && (
            <TextField
              label="Custom tag"
              fullWidth
              size="small"
              variant="outlined"
              value={otherInterest}
              onChange={(e) => setOtherInterest(e.target.value)}
              sx={{ mb: 3, mt: -1.5 }}
            />
          )}

          {/* ── 5.5 SPACE GOAL — one tap, presets the tool loadout ── */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
            What's this space for?
          </Typography>
          <Typography variant="body2" sx={{ color: '#888', mb: 1.25 }}>
            You can always change it up later.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {spaceGoalOptions.map(g => (
              <Chip
                key={g.key}
                label={`${g.emoji} ${g.label}`}
                color={spaceGoal === g.key ? 'primary' : 'default'}
                variant={spaceGoal === g.key ? 'filled' : 'outlined'}
                onClick={() => setSpaceGoal(g.key)}
              />
            ))}
          </Box>
          <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 3, minHeight: '1.2em' }}>
            {spaceGoalOptions.find(g => g.key === spaceGoal)?.blurb || ''}
          </Typography>

          {/* ── 6. SUBMIT ── */}
          <Button
            variant="contained"
            color={isSubmitting ? "success" : "primary"}
            fullWidth
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 700,
              borderRadius: 2,
              transition: 'all 0.3s ease',
              ...(isSubmitting && {
                backgroundColor: '#4caf50',
                '&.Mui-disabled': {
                  backgroundColor: '#4caf50',
                  color: '#fff'
                }
              })
            }}
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {isSubmitting ? funMessage : 'Launch 🚀'}
          </Button>

        </Paper>
      </Container>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CustomSpaceDoor;
